import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Sparkles, Clock, Users, ChevronRight, Lock, Star, Loader2,
  ChevronDown, Heart, Activity, Scale, Apple, Beef, Fish, Egg, Wheat, Milk,
  UtensilsCrossed, Home, ShoppingBag, PieChart, MapPin, Info, Leaf
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { SUITE_NAME, TIER_NAMES } from '@/branding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Program categories structure
const PROGRAM_CATEGORIES = [
  {
    id: 'wellness',
    name: TIER_NAMES.wellness,
    description: 'General wellness and healthy eating programs',
    subcategories: [
      { id: 'wellness-markers', name: 'My Wellness Markers', icon: Leaf }
    ]
  },
  {
    id: 'cdm',
    name: TIER_NAMES.cdm,
    description: null,
    subcategories: [
      { id: 'low-salt', name: 'Low Salt Program', icon: Heart },
      { id: 'sugar-homeostasis', name: 'Sugar Homeostasis Program', icon: Activity },
      { id: 'lipid-target', name: 'Lipid Target Program', icon: Scale },
      { id: 'fluid-balance', name: 'Fluid Balance Program', icon: Activity }
    ]
  },
  {
    id: 'weight-loss',
    name: TIER_NAMES.weight_loss,
    description: 'Weight management and loss programs',
    subcategories: null
  }
];

const MyInsulPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  
  // Programs dropdown state
  const [programsOpen, setProgramsOpen] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const dropdownRef = useRef(null);
  
  // Dietary analysis state
  const [dietaryAnalysis, setDietaryAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [selectedCuisine, setSelectedCuisine] = useState(null);
  const [cuisineDetail, setCuisineDetail] = useState(null);
  const [loadingCuisineDetail, setLoadingCuisineDetail] = useState(false);

  const fetchDietaryAnalysis = useCallback(async () => {
    setLoadingAnalysis(true);
    try {
      const response = await axios.get(`${API_URL}/api/myinsul/dietary-analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDietaryAnalysis(response.data);
    } catch (error) {
      console.error('Failed to fetch dietary analysis:', error);
    } finally {
      setLoadingAnalysis(false);
    }
  }, [token]);

  const checkAccess = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/myinsul/access-check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasAccess(response.data.has_access);
      if (response.data.has_access) {
        fetchDietaryAnalysis();
      }
    } catch (error) {
      setHasAccess(false);
    } finally {
      setCheckingAccess(false);
      setLoading(false);
    }
  }, [token, fetchDietaryAnalysis]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProgramsOpen(false);
        setActiveCategoryId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCuisineDetail = async (cuisineName) => {
    setSelectedCuisine(cuisineName);
    setLoadingCuisineDetail(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/myinsul/cuisine-detail/${encodeURIComponent(cuisineName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCuisineDetail(response.data);
    } catch (error) {
      console.error('Failed to fetch cuisine detail:', error);
    } finally {
      setLoadingCuisineDetail(false);
    }
  };

  const handleCategoryClick = (category) => {
    if (category.subcategories) {
      setActiveCategoryId(activeCategoryId === category.id ? null : category.id);
    } else {
      // Navigate to program category
      navigate(`/myinsul/category/${category.id}`);
      setProgramsOpen(false);
    }
  };

  const handleSubcategoryClick = (categoryId, subcategory) => {
    // Special handling for wellness markers - direct route
    if (subcategory.id === 'wellness-markers') {
      navigate('/myinsul/wellness-markers');
    } else {
      navigate(`/myinsul/category/${categoryId}/${subcategory.id}`);
    }
    setProgramsOpen(false);
    setActiveCategoryId(null);
  };

  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl shadow-xl p-8 max-w-md text-center"
        >
          <Lock className="w-16 h-16 mx-auto text-amber-500 mb-4" />
          <h1 className="text-2xl font-bold mb-2">{SUITE_NAME} Beta</h1>
          <p className="text-muted-foreground mb-6">
            {SUITE_NAME} is currently in beta testing. Access is restricted to invited users only.
          </p>
          <Button onClick={() => navigate('/dashboard')} className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Return to Dashboard
          </Button>
        </motion.div>
      </div>
    );
  }

  const analysis = dietaryAnalysis?.analysis;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/dashboard')}
                className="rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                    {SUITE_NAME}
                  </h1>
                  <p className="text-xs text-muted-foreground">Premium Programs</p>
                </div>
              </div>
              
              {/* Programs Dropdown */}
              <div className="relative ml-6" ref={dropdownRef}>
                <Button
                  variant="outline"
                  onClick={() => setProgramsOpen(!programsOpen)}
                  className="rounded-full px-4 py-2 font-semibold border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                  data-testid="programs-dropdown"
                >
                  Programs
                  <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${programsOpen ? 'rotate-180' : ''}`} />
                </Button>
                
                <AnimatePresence>
                  {programsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-border/50 overflow-hidden z-50"
                    >
                      {PROGRAM_CATEGORIES.map((category) => (
                        <div key={category.id}>
                          <button
                            onClick={() => handleCategoryClick(category)}
                            className="w-full px-4 py-3 text-left hover:bg-emerald-50 flex items-center justify-between transition-colors"
                            data-testid={`program-category-${category.id}`}
                          >
                            <div>
                              <p className="font-semibold text-gray-800">{category.name}</p>
                              <p className="text-xs text-muted-foreground">{category.description}</p>
                            </div>
                            {category.subcategories && (
                              <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${
                                activeCategoryId === category.id ? 'rotate-90' : ''
                              }`} />
                            )}
                          </button>
                          
                          {/* Subcategories */}
                          <AnimatePresence>
                            {category.subcategories && activeCategoryId === category.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="bg-emerald-50/50 overflow-hidden"
                              >
                                {category.subcategories.map((sub) => (
                                  <button
                                    key={sub.id}
                                    onClick={() => handleSubcategoryClick(category.id, sub)}
                                    className="w-full px-6 py-2.5 text-left hover:bg-emerald-100 flex items-center gap-3 transition-colors"
                                    data-testid={`program-subcategory-${sub.id}`}
                                  >
                                    <sub.icon className="w-4 h-4 text-emerald-600" />
                                    <span className="text-sm font-medium text-gray-700">{sub.name}</span>
                                  </button>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-xs bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-medium">
                Beta Access
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* About Me Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
              <Info className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">About Me</h2>
              <p className="text-sm text-muted-foreground">Your dietary analysis from the past month</p>
            </div>
          </div>

          {loadingAnalysis ? (
            <div className="bg-white rounded-3xl shadow-lg p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
              <p className="text-muted-foreground">Analyzing your dietary patterns...</p>
            </div>
          ) : !dietaryAnalysis?.has_data ? (
            <div className="bg-white rounded-3xl shadow-lg p-12 text-center">
              <Apple className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Data Available</h3>
              <p className="text-muted-foreground">
                {dietaryAnalysis?.message || "Start logging your meals to see your dietary analysis"}
              </p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {/* Dietary Preferences */}
              <div className="bg-white rounded-3xl shadow-lg p-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <UtensilsCrossed className="w-5 h-5 text-emerald-600" />
                  Dietary Preferences
                </h3>
                
                {/* Cuisine Breakdown */}
                {analysis?.cuisine_breakdown?.cuisines && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-muted-foreground mb-3">Cuisine Breakdown</h4>
                    <div className="space-y-2">
                      {analysis.cuisine_breakdown.cuisines.map((cuisine) => (
                        <button
                          key={`cuisine-${cuisine.name}`}
                          onClick={() => fetchCuisineDetail(cuisine.name)}
                          className="w-full text-left hover:bg-emerald-50 rounded-xl p-3 transition-colors"
                          data-testid={`cuisine-${cuisine.name}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-emerald-600" />
                              {cuisine.name}
                            </span>
                            <span className="text-emerald-600 font-bold">{cuisine.percentage}%</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-emerald-400 to-teal-500 h-2 rounded-full transition-all"
                              style={{ width: `${cuisine.percentage}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Click to see regional breakdown →
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Food Categories */}
                {analysis?.food_categories && (
                  <div>
                    <h4 className="text-sm font-semibold text-muted-foreground mb-3">Food Categories</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: 'fruits_vegetables', label: 'Fruits & Vegetables', icon: Apple, color: 'text-green-600' },
                        { key: 'dairy', label: 'Dairy', icon: Milk, color: 'text-blue-600' },
                        { key: 'grains', label: 'Grains', icon: Wheat, color: 'text-amber-600' },
                        { key: 'white_meat', label: 'White Meat', icon: Leaf, color: 'text-orange-600' },
                        { key: 'red_meat', label: 'Red Meat', icon: Beef, color: 'text-red-600' },
                        { key: 'seafood', label: 'Seafood', icon: Fish, color: 'text-cyan-600' },
                        { key: 'eggs', label: 'Eggs', icon: Egg, color: 'text-yellow-600' }
                      ].map((cat) => (
                        <div key={cat.key} className="bg-gray-50 rounded-xl p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <cat.icon className={`w-4 h-4 ${cat.color}`} />
                            <span className="text-xs font-medium">{cat.label}</span>
                          </div>
                          <span className="text-lg font-bold">{analysis.food_categories[cat.key]}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Consumption Patterns */}
              <div className="space-y-6">
                {/* Processed Food */}
                <div className="bg-white rounded-3xl shadow-lg p-6">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-orange-600" />
                    Processed Food Consumption
                  </h3>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="w-full bg-gray-100 rounded-full h-4">
                        <div 
                          className="bg-gradient-to-r from-orange-400 to-red-500 h-4 rounded-full transition-all"
                          style={{ width: `${analysis?.consumption_patterns?.processed_food || 0}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-2xl font-bold text-orange-600">
                      {analysis?.consumption_patterns?.processed_food || 0}%
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    of your meals contained processed foods
                  </p>
                </div>

                {/* Takeaway/Restaurant */}
                <div className="bg-white rounded-3xl shadow-lg p-6">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <UtensilsCrossed className="w-5 h-5 text-purple-600" />
                    Takeaway/Restaurant Food
                  </h3>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="w-full bg-gray-100 rounded-full h-4">
                        <div 
                          className="bg-gradient-to-r from-purple-400 to-pink-500 h-4 rounded-full transition-all"
                          style={{ width: `${analysis?.consumption_patterns?.takeaway_restaurant || 0}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-2xl font-bold text-purple-600">
                      {analysis?.consumption_patterns?.takeaway_restaurant || 0}%
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    of your meals were from takeaway or restaurants
                  </p>
                </div>

                {/* Home Cooked */}
                <div className="bg-white rounded-3xl shadow-lg p-6">
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Home className="w-5 h-5 text-emerald-600" />
                    Home Cooked Food
                  </h3>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="w-full bg-gray-100 rounded-full h-4">
                        <div 
                          className="bg-gradient-to-r from-emerald-400 to-teal-500 h-4 rounded-full transition-all"
                          style={{ width: `${analysis?.consumption_patterns?.home_cooked || 0}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-2xl font-bold text-emerald-600">
                      {analysis?.consumption_patterns?.home_cooked || 0}%
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    of your meals were home cooked
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Cuisine Detail Modal */}
          <AnimatePresence>
            {selectedCuisine && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6"
                onClick={() => { setSelectedCuisine(null); setCuisineDetail(null); }}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  className="bg-white rounded-3xl shadow-xl p-6 max-w-lg w-full"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-emerald-600" />
                    {selectedCuisine} Regional Breakdown
                  </h3>
                  
                  {loadingCuisineDetail ? (
                    <div className="py-8 text-center">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary mb-2" />
                      <p className="text-sm text-muted-foreground">Analyzing regional patterns...</p>
                    </div>
                  ) : cuisineDetail?.regions?.length > 0 ? (
                    <div className="space-y-4">
                      {cuisineDetail.regions.map((region) => (
                        <div key={`region-${region.name}`} className="bg-emerald-50 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold">{region.name}</span>
                            <span className="text-emerald-600 font-bold">{region.percentage}%</span>
                          </div>
                          <div className="w-full bg-white rounded-full h-2 mb-2">
                            <div 
                              className="bg-gradient-to-r from-emerald-400 to-teal-500 h-2 rounded-full"
                              style={{ width: `${region.percentage}%` }}
                            />
                          </div>
                          {region.dishes?.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              Dishes: {region.dishes.join(', ')}
                            </p>
                          )}
                        </div>
                      ))}
                      {cuisineDetail.description && (
                        <p className="text-sm text-muted-foreground mt-4">
                          {cuisineDetail.description}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-muted-foreground py-4">
                      No regional data available for this cuisine.
                    </p>
                  )}
                  
                  <Button 
                    onClick={() => { setSelectedCuisine(null); setCuisineDetail(null); }}
                    className="w-full mt-4 rounded-full"
                    variant="outline"
                  >
                    Close
                  </Button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Analysis Insights */}
          {analysis?.insights && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl p-6 text-white"
            >
              <h4 className="font-bold mb-2 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                AI Insights
              </h4>
              <p className="text-emerald-50">{analysis.insights}</p>
            </motion.div>
          )}
        </motion.div>

        {/* Week Range Info */}
        {dietaryAnalysis?.week_range && (
          <p className="text-center text-sm text-muted-foreground mb-8">
            Analysis based on {dietaryAnalysis.total_entries} meals from {dietaryAnalysis.week_range.start} to {dietaryAnalysis.week_range.end}
          </p>
        )}
      </main>
    </div>
  );
};

export default MyInsulPage;
